/**
 * Coupon eligibility / discount math for the `exclude_coupons_online` product field.
 *
 *   exclude_coupons_online = 0  -> coupons & discount codes MAY apply
 *   exclude_coupons_online = 1  -> coupons & discount codes MUST NOT apply
 *
 * This module is intentionally dependency-free (no axios/uuid/redux) so the money
 * math is trivially unit-testable in isolation. It is re-exported from utils.js
 * so existing `import { ... } from '.../utils'` call sites keep working.
 */

// Local 2-decimal rounder (mirror of utils.getRoundValue, kept here to stay
// dependency-free). Returns 0 for falsy / NaN input.
const round2 = (value) =>
  value && !isNaN(value)
    ? parseFloat((Math.round(value * 100) / 100).toFixed(2))
    : 0;

// Only an explicit numeric 1 excludes the product. Number() normalizes "1",
// "1.0", " 1", 1 and true -> 1 (excluded), while undefined/null/""/"abc" -> NaN/0
// (eligible). This fails *closed* for malformed flags: anything we can't read as
// exactly 1 is treated as coupon-eligible only if it isn't 1, so a stray value
// never silently strips a coupon, and only a real 1 ever excludes.
export const isProductCouponEligible = (product) =>
  Number(product?.exclude_coupons_online) !== 1;

export const isMkonnektDiscountedItem = (item) =>
  Boolean(item?.mkonnektDiscountApplied || item?.isTobaccoDiscountItem);

export const isCartItemCouponEligible = (item) =>
  !isMkonnektDiscountedItem(item) &&
  isProductCouponEligible(item?.product);

/**
 * True when `coupon` is a usable coupon object. `flag` is the discriminator the
 * discount math depends on (0 = percentage, otherwise fixed), so its presence -
 * not `id` - is the reliable "this is a real coupon to apply" signal. An empty
 * string / null (the no-coupon state) returns false.
 */
export const isApplicableCoupon = (coupon) =>
  !!coupon && coupon.flag !== undefined && coupon.flag !== null;

/**
 * Sum of (already qty-weighted, post bogo/mix-match) prices of coupon-eligible
 * items only. Excluded products still count toward the plain subtotal elsewhere
 * - never here.
 */
export const couponEligibleSubtotal = (cartItems = []) =>
  round2(
    (cartItems || []).reduce(
      (acc, item) =>
        isCartItemCouponEligible(item)
          ? acc + (parseFloat(item?.price) || 0)
          : acc,
      0,
    ),
  );

/**
 * Canonical coupon discount amount computed against an eligible base only.
 *   flag === '0' -> percentage coupon (capped by maximum_discount when > 0)
 *   otherwise    -> fixed amount, capped at the eligible base (full amount otherwise)
 * Returns 0 when the eligible base is <= 0 (e.g. every product excluded).
 */
export const calculateCouponDiscount = (couponData, eligibleBase) => {
  const base = parseFloat(eligibleBase) || 0;
  if (!couponData || base <= 0) return 0;

  const discountVal = parseFloat(couponData.discount) || 0;

  if (String(couponData.flag) === '0') {
    const totalDiscount = round2((base / 100) * discountVal);
    const maxDiscount = parseFloat(couponData.maximum_discount);
    if (!isNaN(maxDiscount) && maxDiscount > 0 && totalDiscount > maxDiscount) {
      return round2(maxDiscount);
    }
    return round2(totalDiscount);
  }

  // fixed amount coupon - never discount more than the eligible products are worth
  return round2(Math.min(discountVal, base));
};

/**
 * Percentage to deduct from each eligible item so that
 * eligibleBase * pct / 100 === calculateCouponDiscount(coupon, eligibleBase).
 * Keeps per-item penny-fix math consistent with the order-level discount.
 */
export const couponDeductionPercentage = (couponData, eligibleBase) => {
  const base = parseFloat(eligibleBase) || 0;
  if (base <= 0) return 0;
  return (calculateCouponDiscount(couponData, base) / base) * 100;
};
